import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { emailReg, StringRegex } from "utils/Regex";

/**
 * CustomMemberFields
 * Renders a dynamic list of member rows (admin or finance).
 * Each row has: Name, Email, Phone (with country code).
 * Validates: name (text only), email (format), phone (10 digits).
 *
 * Props:
 *   members       – array of { name, email, phone, countryCode }
 *   setMembers    – setter for the array
 *   register      – react-hook-form register (used only to register field names)
 *   fieldPrefix   – 'admin' | 'finance'
 *   disabled      – boolean (when parent-company switch is on)
 *   sectionLabel  – label shown above rows
 *   isRequired    – whether name/email/phone are required (default true for admin, false for finance)
 */
const CustomMemberFields = ({
  members,
  setMembers,
  fieldPrefix,
  disabled,
  sectionLabel,
  isRequired = true,
}) => {
  // fieldErrors: array of { name, email, phone } error strings per row
  const [fieldErrors, setFieldErrors] = useState(() =>
    members.map(() => ({ name: "", email: "", phone: "" }))
  );

  // Reset validation errors when members are replaced externally (e.g. parent toggle)
  useEffect(() => {
    setFieldErrors(members.map(() => ({ name: "", email: "", phone: "" })));
  }, [disabled, members.length]);

  const setRowError = (index, field, message) => {
    setFieldErrors((prev) => {
      const updated = [...prev];
      updated[index] = { ...(updated[index] || {}), [field]: message };
      return updated;
    });
  };

  const validateName = (value, index) => {
    if (isRequired && !value?.trim()) {
      setRowError(index, "name", "This field is required");
      return false;
    }
    if (value && StringRegex.test(value)) {
      setRowError(index, "name", "Name should contain text only");
      return false;
    }
    setRowError(index, "name", "");
    return true;
  };

  const validateEmail = (value, index) => {
    if (isRequired && !value?.trim()) {
      setRowError(index, "email", "This field is required");
      return false;
    }
    if (value && !emailReg.test(value)) {
      setRowError(index, "email", "Enter a valid email address");
      return false;
    }
    setRowError(index, "email", "");
    return true;
  };

  const validatePhone = (value, index) => {
    const digits = (value || "").replace(/\D/g, "");
    if (isRequired && !digits) {
      setRowError(index, "phone", "This field is required");
      return false;
    }
    if (digits && digits.length !== 10) {
      setRowError(index, "phone", "Mobile number must be 10 digits");
      return false;
    }
    setRowError(index, "phone", "");
    return true;
  };

  const handleAdd = () => {
    setMembers([...members, { name: "", email: "", phone: "", countryCode: "+91" }]);
    setFieldErrors((prev) => [...prev, { name: "", email: "", phone: "" }]);
  };

  const handleDelete = (index) => {
    setMembers(members.filter((_, i) => i !== index));
    setFieldErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const updated = [...members];
    updated[index] = { ...updated[index], [field]: value };
    setMembers(updated);

    // Validate on change
    if (field === "name") validateName(value, index);
    else if (field === "email") validateEmail(value, index);
    else if (field === "phone") validatePhone(value, index);
  };

  return (
    <Box className="customMemberFields" sx={{ width: "100%" }}>
      {members.map((member, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          {members.length > 1 && (
            <Typography variant="body2" className="fw600 colorBlack mb10">
              {sectionLabel} {index + 1}
            </Typography>
          )}
          <Grid container spacing={2} columns={{ xs: 1, sm: 3, md: 12 }}>
            {/* Name */}
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="body2" className="flex fw600 colorBlack mb10">
                Name {isRequired && <span className="colorRed">&nbsp;*</span>}
              </Typography>
              <TextField
                fullWidth
                placeholder={`Enter ${sectionLabel} Name`}
                disabled={disabled}
                value={member.name}
                onChange={(e) => handleChange(index, "name", e.target.value)}
                onBlur={(e) => validateName(e.target.value, index)}
                error={!!fieldErrors[index]?.name}
                sx={{ '& .MuiInputBase-root': { backgroundColor: disabled ? '#f5f5f5' : '#ffffff' } }}
              />
              {fieldErrors[index]?.name && (
                <Typography variant="body2" className="errorMessage">
                  {fieldErrors[index].name}
                </Typography>
              )}
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="body2" className="flex fw600 colorBlack mb10">
                Email ID {isRequired && <span className="colorRed">&nbsp;*</span>}
              </Typography>
              <TextField
                fullWidth
                placeholder={`Enter ${sectionLabel} Email`}
                disabled={disabled}
                value={member.email}
                onChange={(e) => handleChange(index, "email", e.target.value)}
                onBlur={(e) => validateEmail(e.target.value, index)}
                error={!!fieldErrors[index]?.email}
                sx={{ '& .MuiInputBase-root': { backgroundColor: disabled ? '#f5f5f5' : '#ffffff' } }}
              />
              {fieldErrors[index]?.email && (
                <Typography variant="body2" className="errorMessage">
                  {fieldErrors[index].email}
                </Typography>
              )}
            </Grid>

            {/* Phone with Country Code */}
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="body2" className="flex fw600 colorBlack mb10">
                Phone No. {isRequired && <span className="colorRed">&nbsp;*</span>}
              </Typography>
              <Box className="flex commonMultiform">
                <Box className="customCountryCode">
                  <FormControl className="br10">
                    <Select
                      disabled={disabled}
                      value={member.countryCode || "+91"}
                      onChange={(e) => handleChange(index, "countryCode", e.target.value)}
                    >
                      <MenuItem value="+91">+91</MenuItem>
                      <MenuItem value="+1">+1</MenuItem>
                      <MenuItem value="+44">+44</MenuItem>
                      <MenuItem value="+971">+971</MenuItem>
                      <MenuItem value="+65">+65</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <FormControl fullWidth className="commonFormControl">
                <TextField
                  fullWidth
                  placeholder={`Enter ${sectionLabel} Phone`}
                  disabled={disabled}
                  value={member.phone}
                  type="number"
                  onChange={(e) => {
                    // block non-numeric and 'e'
                    const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                    handleChange(index, "phone", val);
                  }}
                  onBlur={(e) => validatePhone(e.target.value, index)}
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
                  error={!!fieldErrors[index]?.phone}
                  sx={{ '& .MuiInputBase-root': { backgroundColor: disabled ? '#f5f5f5' : '#ffffff' } }}
                />
                </FormControl>
              </Box>
              {fieldErrors[index]?.phone && (
                <Typography variant="body2" className="errorMessage">
                  {fieldErrors[index].phone}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Delete row button – only show when more than 1 member */}
          {members.length > 1 && (
            <Box className="justifyEnd" sx={{ mt: 1 }}>
              <Typography
                variant="body2"
                className="fw400 alignItem onBoardDeleteBtn"
                role="presentation"
                onClick={() => !disabled && handleDelete(index)}
                sx={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1 }}
              >
                <span className="pr10">
                  <DeleteOutlinedIcon role="presentation" />
                </span>
                Delete
              </Typography>
            </Box>
          )}

          {/* Dashed divider between rows */}
          {index < members.length - 1 && (
            <Box className="dashedBorder mt20 mb10" />
          )}
        </Box>
      ))}

      {/* Add button */}
      <Box className="justifyEnd" sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          className="fw400 pt10 justifyCenter"
          role="presentation"
          onClick={() => !disabled && handleAdd()}
          sx={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1 }}
        >
          <Typography variant="head4" className="colorBlue pr10" component="span">
            +
          </Typography>
          Add {sectionLabel}
        </Typography>
      </Box>
    </Box>
  );
};

export default CustomMemberFields;
